import { useEffect, useState } from "react";

import { formatChinaDateTime } from "../time";
import {
  billingReconciliationExportUrl,
  listBillingReconciliation,
  listBillingReconciliationSubjects,
  PLATFORM_TOKEN_KEY,
  type BillingReconciliationLine,
  type BillingReconciliationOption,
  type BillingReconciliationOptions,
  type BillingReconciliationPage,
  type ReconciliationDimension,
} from "./api";

const PAGE_SIZE = 10;

function chinaMonthRange(now = new Date()): { dateFrom: string; dateTo: string } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Shanghai", year: "numeric", month: "2-digit",
  }).formatToParts(now);
  const year = Number(parts.find((part) => part.type === "year")?.value);
  const month = Number(parts.find((part) => part.type === "month")?.value);
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const prefix = `${year}-${String(month).padStart(2, "0")}`;
  return { dateFrom: `${prefix}-01`, dateTo: `${prefix}-${String(lastDay).padStart(2, "0")}` };
}

function pages(total: number): number {
  return Math.max(1, Math.ceil(total / PAGE_SIZE));
}

function formatAmount(value: string | null): string {
  if (value === null) return "成本未配置";
  return `¥${Number(value).toLocaleString("zh-CN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function lineStatus(line: BillingReconciliationLine): string {
  return line.billing_status;
}

export function BillingReconciliation() {
  const month = chinaMonthRange();
  const [dimension, setDimension] = useState<ReconciliationDimension>("institution");
  const [subjects, setSubjects] = useState<BillingReconciliationOption[]>([]);
  const [products, setProducts] = useState<BillingReconciliationOption[]>([]);
  const [subject, setSubject] = useState("");
  const [dateFrom, setDateFrom] = useState(month.dateFrom);
  const [dateTo, setDateTo] = useState(month.dateTo);
  const [productCode, setProductCode] = useState("");
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<BillingReconciliationPage | null>(null);
  const [loadingSubjects, setLoadingSubjects] = useState(true);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [reloadNonce, setReloadNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoadingSubjects(true);
    setError("");
    setSubjects([]);
    setSubject("");
    setResult(null);
    void listBillingReconciliationSubjects(dimension)
      .then((options: BillingReconciliationOptions) => {
        if (cancelled) return;
        setSubjects(options.subjects);
        setProducts(options.products);
        setSubject(options.subjects[0]?.key ?? "");
      })
      .catch((reason) => !cancelled && setError(
        reason instanceof Error ? reason.message : "对账对象加载失败",
      ))
      .finally(() => !cancelled && setLoadingSubjects(false));
    return () => { cancelled = true; };
  }, [dimension]);

  useEffect(() => {
    if (!subject || !dateFrom || !dateTo) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    void listBillingReconciliation({ dimension, subject, dateFrom, dateTo, productCode, page })
      .then((loaded) => {
        if (cancelled) return;
        setResult(loaded);
        if (page > pages(loaded.total)) setPage(pages(loaded.total));
      })
      .catch((reason) => !cancelled && setError(
        reason instanceof Error ? reason.message : "对账明细加载失败",
      ))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [dimension, subject, dateFrom, dateTo, productCode, page, reloadNonce]);

  function resetPage(action: () => void) {
    action();
    setPage(1);
  }

  async function exportStatement() {
    if (!subject || exporting) return;
    setExporting(true);
    setError("");
    try {
      const token = sessionStorage.getItem(PLATFORM_TOKEN_KEY);
      const response = await fetch(
        billingReconciliationExportUrl({ dimension, subject, dateFrom, dateTo, productCode }),
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!response.ok) throw new Error("对账单导出失败，请稍后重试");
      const link = document.createElement("a");
      link.href = URL.createObjectURL(await response.blob());
      link.download = "对账单.xlsx";
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(link.href), 0);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "对账单导出失败，请稍后重试");
    } finally {
      setExporting(false);
    }
  }

  const summaryLabel = dimension === "supplier" ? "供应商成本" : "本期应收";
  const total = result?.total ?? 0;
  const visibleItems = result?.items ?? [];

  return (
    <section className="platform-billing-reconciliation" aria-label="计费对账">
      <div className="platform-panel">
        <div className="platform-billing-reconciliation-toolbar">
          <div className="platform-billing-dimension" role="group" aria-label="对账维度">
            <button className={dimension === "institution" ? "is-active" : ""} type="button" onClick={() => resetPage(() => setDimension("institution"))}>机构</button>
            <button className={dimension === "personal_lawyer" ? "is-active" : ""} type="button" onClick={() => resetPage(() => setDimension("personal_lawyer"))}>个人律师</button>
            <button className={dimension === "supplier" ? "is-active" : ""} type="button" onClick={() => resetPage(() => setDimension("supplier"))}>供应商</button>
          </div>
          <label>对账对象<select aria-label="对账对象" value={subject} disabled={loadingSubjects || subjects.length === 0} onChange={(event) => resetPage(() => setSubject(event.target.value))}>
            {subjects.length === 0 && <option value="">暂无可对账对象</option>}
            {subjects.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
          </select></label>
          <label>对账期间<input aria-label="对账开始日期" type="date" value={dateFrom} onChange={(event) => resetPage(() => setDateFrom(event.target.value))} /></label>
          <span className="platform-billing-date-separator">至</span>
          <input aria-label="对账结束日期" type="date" value={dateTo} onChange={(event) => resetPage(() => setDateTo(event.target.value))} />
          <label>服务类型<select aria-label="服务类型" value={productCode} onChange={(event) => resetPage(() => setProductCode(event.target.value))}>
            <option value="">全部服务</option>
            {products.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
          </select></label>
          <button className="platform-primary-button" type="button" disabled={loading || !subject} onClick={() => { setPage(1); setReloadNonce((value) => value + 1); }}>查询</button>
          <button className="platform-billing-export" type="button" disabled={loading || !subject || exporting} onClick={() => void exportStatement()}>{exporting ? "导出中…" : "导出对账单"}</button>
        </div>

        {result && <div className="platform-billing-summary" aria-label="对账汇总">
          <span><small>本期对账对象</small><strong>{result.summary.subject_label}</strong></span>
          <span><small>已计费订单</small><strong>{result.summary.billed_count} 笔</strong></span>
          <span><small>{summaryLabel}</small><strong>{formatAmount(result.summary.amount)}</strong></span>
          <span><small>对账结果</small><strong className="is-ready">可出账</strong></span>
        </div>}

        <div className="platform-table-wrap">
          <table className="platform-account-table platform-billing-table">
            <thead><tr><th>计费时间</th><th>订单编号</th><th>查询对象</th><th>服务</th><th>计费方式</th><th>支付方式</th><th>计费金额</th><th>状态</th></tr></thead>
            <tbody>
              {!loading && !error && visibleItems.map((line) => <tr key={`${line.order_id}-${line.billed_at}-${line.product_code}`}>
                <td>{formatChinaDateTime(line.billed_at)}</td><td><strong>{line.order_id}</strong></td><td>{line.subject_summary}</td><td>{line.product_name}</td><td>{line.billing_mode}</td><td>{line.payment_method ?? "—"}</td><td>{formatAmount(line.amount)}</td><td>{lineStatus(line)}</td>
              </tr>)}
              {(loading || loadingSubjects) && <tr><td className="platform-table-state" colSpan={8}>正在加载对账明细…</td></tr>}
              {!loading && !loadingSubjects && error && <tr><td className="platform-table-state is-error" colSpan={8}>{error}<button type="button" onClick={() => setReloadNonce((value) => value + 1)}>重新加载</button></td></tr>}
              {!loading && !loadingSubjects && !error && visibleItems.length === 0 && <tr><td className="platform-table-state" colSpan={8}>当前筛选条件下暂无计费订单</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="platform-pagination"><span>共 {total} 条，每页 {PAGE_SIZE} 条</span><div><button type="button" disabled={page <= 1 || loading} onClick={() => setPage((current) => current - 1)}>上一页</button><span>{page} / {pages(total)}</span><button type="button" disabled={page >= pages(total) || loading} onClick={() => setPage((current) => current + 1)}>下一页</button></div></div>
      </div>
    </section>
  );
}
